import { FunctionComponent, MouseEvent, ReactNode } from "react";

import ReactDOM from "react-dom";

import { faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

/**
 * Floating "Tell AI" Generate Messages panel — a centered overlay portal with a
 * collapse button, hosting the shared GenerateBar as children. Extracted from
 * {@link useAddInboxChat} so that large hook stays under the file-length gate.
 *
 * Closing while a generation is running is safe: the job runs server-side and
 * the page-level copywriting socket keeps tracking it, so the result still
 * lands in the chat (and in list_person) after the panel is gone.
 */
export const FloatingGeneratePanel: FunctionComponent<{
	onRequestClose: () => void;
	/** Portal target (the extension passes its shadow root). Defaults to `document.body`. */
	portalContainer?: HTMLElement | null;
	/**
	 * Confine the overlay to `portalContainer` (`absolute inset-0`) instead of the viewport
	 * (`fixed inset-0`). The web Lead Panel hosts pass their positioned panel element and set
	 * this, so the scrim dims only the panel and the card centers inside it — a viewport
	 * overlay portaled into the panel's stacking context (z-150) slides *under* higher-z
	 * chrome (the sidebar is z-200) while dimming the rest of the page, and the card floats
	 * detached in the middle of the screen. Full-page chats (Inbox/Compose/ReadyToSend) and
	 * the extension (whose portal host is a 0×0 shadow-root element that `absolute` would
	 * collapse into) keep the viewport overlay.
	 */
	scopeToContainer?: boolean;
	children: ReactNode;
}> = ({ onRequestClose, portalContainer, scopeToContainer = false, children }) => {
	// Close only when clicking directly on the backdrop overlay, not on children.
	const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
		if (event.target === event.currentTarget) {
			onRequestClose();
		}
	};

	return ReactDOM.createPortal(
		<div
			className={classNames(
				scopeToContainer ? "absolute" : "fixed",
				"inset-0 z-[9998] flex items-center justify-center bg-black/30"
			)}
			onClick={handleBackdropClick}
		>
			<div
				className={classNames(
					"relative rounded-lg shadow-xl w-[560px] p-0",
					scopeToContainer ? "max-w-[92%]" : "max-w-[90vw]"
				)}
			>
				<button
					className="absolute top-3 right-3 z-10 p-1.5 rounded-md hover:bg-gray-100 text-text-meta hover:text-text transition-colors cursor-pointer"
					title="Collapse"
					type="button"
					onClick={onRequestClose}
				>
					<Icon className="w-3.5 h-3.5" icon={faChevronUp} />
				</button>
				{children}
			</div>
		</div>,
		portalContainer ?? document.body
	);
};
